import React, { useState } from "react"
import {
  AccountUser, DioboxEvent, EventUser, User,
} from "sharedTypes"
import PermissionsForm, { InitialValuesProps, Mode } from "modals/PermissionsForm"
import { EventUserRole } from "services/permissions"
import { addAccountUser, updateAccountUser } from "context/actions"
import { postAccountUser, postAccountUserDirect, putAccountUser } from "api/accounts"
import { warningToast } from "utilities/toasts"
import { useQueryClient } from "react-query"
import { accountUsersCacheKey } from "queries/accountUsers"
import { currentAccountId } from "services/authentication"
import extendFormValuesWithImages from "utilities/extendFormValuesWithImages"
import { deleteEventUser, postEventUser, putEventUser } from "../api/events"

type Props = {
  onClose: () => void
  currentUser: User
  currentEvent?: DioboxEvent
  accountUser?: AccountUser
}

const AccountUserForm = ({
  onClose, currentUser, currentEvent, accountUser,
}: Props) => {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<Mode>("invite")

  const getEventUser = (eventId: string): (EventUser | undefined) => (
    accountUser?.events.find(({ event }) => event.id === eventId)
  )

  const handleEventUserRoleChange = async ({ eventId, email, role }) => {
    const eventUser = getEventUser(eventId)

    if (!eventId) { return null }
    if (eventUser?.role === role) { return null }
    if (!eventUser && role === EventUserRole.NoAccess) { return null }

    if (!eventUser) {
      return postEventUser(eventId, { email, role })
    }

    if (role === EventUserRole.NoAccess) {
      return deleteEventUser(eventId, eventUser.id)
    }

    return putEventUser(eventId, eventUser.id, { role })
  }

  const onSubmit = async (values) => {
    const {
      admin, canCreateEvents, firstName, lastName, phone, photo,
    } = values

    if (accountUser) {
      const payload: Record<string, any> = {
        ...accountUser,
        admin,
        canCreateEvents,
        firstName,
        lastName,
        phone,
      }
      if (photo instanceof File) payload.photo = photo
      else if (photo === null) payload.photo = ""
      const { data: updatedAccountUser } = await putAccountUser(payload as any)
      await updateAccountUser(queryClient, updatedAccountUser)
      const updatedEventUser = await handleEventUserRoleChange(values)
      if (updatedEventUser) {
        await queryClient.invalidateQueries(accountUsersCacheKey(currentAccountId()))
      }
    } else if (mode === "create") {
      const { data: newAccountUser } = await postAccountUserDirect(values)
      await addAccountUser(queryClient, newAccountUser)
      try {
        const createdEventUser = await handleEventUserRoleChange(values)
        if (createdEventUser) {
          await queryClient.invalidateQueries(accountUsersCacheKey(currentAccountId()))
        }
      } catch {
        warningToast({ title: "User created but event role could not be assigned." })
      }
    } else {
      const createdEventUser = await handleEventUserRoleChange(values)

      if (!createdEventUser) {
        const { data: newAccountUser } = await postAccountUser(values)
        await addAccountUser(queryClient, newAccountUser)
      } else {
        await queryClient.invalidateQueries(accountUsersCacheKey(currentAccountId()))
      }
    }

    onClose()
  }

  const initialValues: InitialValuesProps = {
    admin: false,
    canCreateEvents: false,
    ...extendFormValuesWithImages({}, ["photo"]),
  }

  if (currentEvent) {
    initialValues.eventId = currentEvent.id
    const eventUser = getEventUser(currentEvent.id)
    initialValues.role = eventUser?.role ?? EventUserRole.NoAccess
  }

  if (accountUser) {
    const { user } = accountUser
    initialValues.email = accountUser.email ?? user?.email
    initialValues.admin = accountUser.admin
    initialValues.canCreateEvents = accountUser.canCreateEvents
    initialValues.firstName = user?.firstName
    initialValues.lastName = user?.lastName
    initialValues.phone = user?.phone
    initialValues.photoUrl = user?.photoUrl
  }

  return (
    <PermissionsForm
      user={accountUser?.user}
      initialValues={initialValues}
      onSubmit={onSubmit}
      onClose={onClose}
      currentUser={currentUser}
      mode={mode}
      onModeChange={setMode}
    />
  )
}

export default AccountUserForm
